'use client';

import './datePicker.scss';

import cx from 'classnames';
import { endOfDay, endOfMonth, isAfter, startOfDay, startOfMonth } from 'date-fns';
import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useClickAway } from 'react-use';

import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { useResponsive } from '@flipster/react/misc/responsive/legacy';
import { convertDate } from '@flipster/utils/datetime';

import { Button } from '../Button';
import { PopupToDrawer } from '../PopupToDrawer';
import CalenderIcon from '../assets/svgs/calendar-days.svg';
import DeleteIcon from '../assets/svgs/delete.svg';
import { SizeType } from '../types/base';
import CustomHeader from './CustomHeader';
import styles from './datePicker.module.scss';
import { getDateString } from './utils';

type PossibleDateType = Date | undefined;

type TabType = 'start' | 'end';
const Tabs = createTabsComponent<TabType>();

interface Props {
  defaultStartDate: PossibleDateType | string;
  defaultEndDate: PossibleDateType | string;
  onSearch: (dates: [PossibleDateType, PossibleDateType]) => void;
  size?: SizeType;
  onOpen?: () => void;
  isMonthly?: boolean;
  minDate?: PossibleDateType;
  maxDate?: PossibleDateType;
  excludeDates?: Date[];
  excludeDateIntervals?: { start: Date; end: Date }[];
  onChangeDate?: ([startDate, endDate]: [PossibleDateType, PossibleDateType]) => void;
  renderTriggerButton?: (props: { onToggle: (e: MouseEvent) => void; isOpen: boolean }) => ReactNode;
  confirmLabel?: string;
  /** When true, the Start tab is disabled and start date cannot be changed. */
  disableStart?: boolean;
  /** When true, the End tab is disabled and end date cannot be changed. */
  disableEnd?: boolean;
}

const TargetMapper = {
  start: {
    index: 0,
    dayFn: startOfDay,
  },
  end: {
    index: 1,
    dayFn: endOfDay,
  },
} as const;

const MonthlyTargetMapper = {
  start: {
    index: 0,
    dayFn: startOfMonth,
  },
  end: {
    index: 1,
    dayFn: endOfMonth,
  },
};

const RangePicker = ({
  defaultStartDate,
  defaultEndDate,
  onSearch,
  size = 'large',
  onOpen,
  isMonthly,
  minDate,
  maxDate,
  excludeDates,
  excludeDateIntervals,
  onChangeDate,
  renderTriggerButton,
  confirmLabel = 'Search',
  disableStart,
  disableEnd,
}: Props) => {
  const datePickerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isShowMonthYear, setIsShowMonthYear] = useState<boolean>(false);
  const [focusTarget, setFocusTarget] = useState<TabType>(disableStart ? 'end' : 'start');
  const [dateRange, setDateRange] = useState<[PossibleDateType, PossibleDateType]>([undefined, undefined]);

  const { isMobile } = useResponsive();

  const [startDate, endDate] = dateRange;

  const allExist = dateRange.every((date) => date);
  const allEmpty = dateRange.every((date) => !date);

  const onToggle = (e: MouseEvent) => {
    e.preventDefault();
    onOpen?.();
    setIsOpen((prevState) => !prevState);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const onChangeHandler = () => {
    if (isShowMonthYear) {
      setIsShowMonthYear(false);
    }
  };

  const handleChangeDate = (nextDate: Date | null) => {
    if (isShowMonthYear) return;
    if (focusTarget === 'start' && disableStart) return;
    if (focusTarget === 'end' && disableEnd) return;
    const { index, dayFn } = isMonthly ? MonthlyTargetMapper[focusTarget] : TargetMapper[focusTarget];
    if (focusTarget === 'start' && !disableEnd) setFocusTarget('end');
    setDateRange((prevState) => {
      const next: [PossibleDateType, PossibleDateType] = [...prevState];
      next[index] = nextDate ? dayFn(nextDate) : undefined;
      // start가 end보다 뒤에 있는 경우 end를 초기화
      if (next[0] && next[1] && isAfter(next[0], next[1])) next[1] = undefined;
      onChangeDate?.(next);
      return next;
    });
  };

  const onClickSearch = () => {
    onSearch(dateRange);
    onClose();
  };

  const clear = () => {
    setDateRange((prev) => {
      const next: [PossibleDateType, PossibleDateType] = [
        disableStart ? prev[0] : undefined,
        disableEnd ? prev[1] : undefined,
      ];
      onChangeDate?.(next);
      return next;
    });
    setFocusTarget(disableStart ? 'end' : 'start');
  };

  useClickAway(datePickerRef, () => {
    if (!isMobile) onClose();
  });

  useEffect(() => {
    if (isOpen) {
      setDateRange([convertDate(defaultStartDate) ?? undefined, convertDate(defaultEndDate) ?? undefined]);
    } else {
      setFocusTarget(disableStart ? 'end' : 'start');
    }
  }, [isOpen, defaultStartDate, defaultEndDate, disableStart]);

  return (
    <div translate='no' className={styles.wrapper} ref={datePickerRef}>
      {renderTriggerButton ? (
        renderTriggerButton({ onToggle, isOpen })
      ) : (
        <Button shape='trans' className={cx(styles.calenderButton, { [styles.isOpen]: isOpen })} onClick={onToggle}>
          <CalenderIcon className={styles[size]} />
        </Button>
      )}
      <PopupToDrawer
        theme='dark'
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        popupClassName={styles.popup}
        withArrow
      >
        <>
          <Tabs
            type='fixed'
            value={focusTarget}
            onChange={(tab) => {
              if (tab === 'start' && disableStart) return;
              if (tab === 'end' && disableEnd) return;
              setFocusTarget(tab);
            }}
          >
            <Tabs.Tab value='start' label='Start' />
            <Tabs.Tab value='end' label='End' />
          </Tabs>
          <section className={styles.picker}>
            <ReactDatePicker
              selected={
                isMonthly
                  ? dateRange[MonthlyTargetMapper[focusTarget].index]
                  : dateRange[TargetMapper[focusTarget].index]
              }
              onChange={onChangeHandler}
              onSelect={handleChangeDate}
              startDate={startDate}
              endDate={endDate}
              selectsStart={focusTarget === 'start'}
              selectsEnd={focusTarget === 'end'}
              minDate={minDate || (focusTarget === 'end' ? startDate : undefined)}
              maxDate={maxDate || new Date()}
              excludeDates={excludeDates}
              excludeDateIntervals={excludeDateIntervals}
              formatWeekDay={(weekDay) => weekDay[0]}
              renderMonthContent={(i, shortMonthText) => `${shortMonthText}.`}
              renderCustomHeader={(props) => (
                <CustomHeader
                  {...props}
                  isShowMonthYear={isShowMonthYear}
                  onClickMonthYear={() => setIsShowMonthYear((prevState) => !prevState)}
                  isMonthly={isMonthly}
                />
              )}
              inline
              showMonthYearPicker={isMonthly || isShowMonthYear}
              disabledKeyboardNavigation
            />

            <div className={styles.fakeInput}>
              <span className={cx(styles.date, { [styles.isExist]: !!startDate })}>
                {getDateString(startDate, 'Start date')}
              </span>
              <span>~</span>
              <span className={cx(styles.date, { [styles.isExist]: !!endDate })}>
                {getDateString(endDate, 'End date')}
              </span>
              <div className={styles.clear}>
                {dateRange.some((date) => date) && (
                  <Button shape='trans' onClick={clear}>
                    <DeleteIcon />
                  </Button>
                )}
              </div>
            </div>
            <footer>
              <div className={styles.actions}>
                <Button shape='tertiary' isFull onClick={onClose}>
                  Close
                </Button>
                <Button shape='primary' isFull onClick={onClickSearch} disabled={!(allExist || allEmpty)}>
                  {confirmLabel}
                </Button>
              </div>
            </footer>
          </section>
        </>
      </PopupToDrawer>
    </div>
  );
};

export default RangePicker;
